import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { SideDrawerLayout } from 'layouts';
import { ErrorOutline, InfoOutlined } from '@mui/icons-material';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	Stack,
	Tooltip,
	Typography
} from '@mui/material';
import { BatchEditPageDetailsProps } from 'models';
import { ConfirmDialog } from 'components/ConfirmDialog';
import { SaveErrorBanner } from 'components/PageDetails/childs';
import { FormattedMessage, useIntl } from 'react-intl';
import SitemapDatePicker from 'components/SitemapDatePicker';
import { SitemapApi } from 'api';
import { composerSettings, helpers, launchdarkly } from '@ddc-webplatform/cmsc-modules';
import { canEditSitemapCollection, drawerWidth, DEFAULT_MAX_BATCH_SIZE, LD_FLAG_BATCH_UPDATE_SIZE_LIMIT } from 'constants/ui';
import { fireTrackableEvent } from 'helpers/analytics';
import { TRACKING_EVENT, TRACKING_FIELD } from 'constants/trackingEvents';
import { BatchSizeLimitDialog } from 'components/BatchSizeLimitDialog';

type FormData = {
	showInHtmlSitemap: boolean;
	showInXmlSitemap: boolean;
	addToSitemapOn: string | null;
	removeFromSitemapOn: string | null;
	noIndex: boolean;
	noFollow: boolean;
};

const createBlankFormData = (): FormData => ({
	showInHtmlSitemap: false,
	showInXmlSitemap: false,
	addToSitemapOn: null,
	removeFromSitemapOn: null,
	noIndex: false,
	noFollow: false
});

const BatchEditPageDetails = ({
	open,
	onClose,
	onHasChangesChange,
	selectedCount,
	pageAliases,
	aliasToPageIdMap,
	onSave
}: BatchEditPageDetailsProps) => {
	const intl = useIntl();
	const [startDateOpen, setStartDateOpen] = useState(false);
	const [endDateOpen, setEndDateOpen] = useState(false);
	const [startDateError, setStartDateError] = useState<string | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
	const [isUpdateLimitDialogOpen, setIsUpdateLimitDialogOpen] = useState(false);
	const [defaultFormData, setDefaultFormData] = useState<FormData>(() =>
		createBlankFormData()
	);

	const rawUpdateLimit = launchdarkly.useFlag(LD_FLAG_BATCH_UPDATE_SIZE_LIMIT);
	const batchUpdateLimit =
		typeof rawUpdateLimit === 'number' ? rawUpdateLimit : DEFAULT_MAX_BATCH_SIZE;
	const [formData, setFormData] = useState<FormData>(() => createBlankFormData());

	// Reset form to blank whenever the drawer opens
	useEffect(() => {
		if (!open) return;
		const blankFormData = createBlankFormData();
		setDefaultFormData(blankFormData);
		setFormData(blankFormData);
		setStartDateError(null);
		setSaveError(null);
	}, [open]);

	const hasChanges = useMemo(() => {
		if (formData.showInHtmlSitemap !== defaultFormData.showInHtmlSitemap)
			return true;
		if (formData.showInXmlSitemap !== defaultFormData.showInXmlSitemap)
			return true;
		if (formData.noIndex !== defaultFormData.noIndex) return true;
		if (formData.noFollow !== defaultFormData.noFollow) return true;
		return false;
	}, [formData, defaultFormData]);

	// Notify parent when unsaved-changes status changes
	useEffect(() => {
		onHasChangesChange?.(hasChanges);
	}, [hasChanges, onHasChangesChange]);

	const endDateError = useMemo<string | null>(() => {
		if (!formData.showInXmlSitemap) return null;
		if (!formData.removeFromSitemapOn || !formData.addToSitemapOn) return null;
		if (
			!dayjs(formData.removeFromSitemapOn).isAfter(
				dayjs(formData.addToSitemapOn)
			)
		) {
			return intl.formatMessage({
				id: 'SITEMAP_END_DATE_INVALID',
				defaultMessage: 'End date must be after the start date'
			});
		}
		return null;
	}, [formData.showInXmlSitemap, formData.removeFromSitemapOn, formData.addToSitemapOn, intl]);

	const handleChange = useCallback(
		(field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
			const { checked } = event.target;
			const isXmlField = field === 'showInXmlSitemap';
			setFormData(prev => {
				const next = { ...prev, [field]: checked };
				// Auto-fill start date when enabling XML so user doesn't have to pick manually.
				// Only set if not already set — preserves any date the user picked earlier.
				if (isXmlField && checked && !prev.addToSitemapOn) {
					next.addToSitemapOn = new Date().toISOString();
				}
				return next;
			});
			if (isXmlField) {
				setStartDateError(null);
			}
		},
		[]
	);

	const handleSave = useCallback(async () => {
		setStartDateError(null);
		if (formData.showInXmlSitemap && !formData.addToSitemapOn) {
			setStartDateError(
				intl.formatMessage({
					id: 'SITEMAP_START_DATE_REQUIRED',
					defaultMessage: 'Start date is required.'
				})
			);
			return;
		}
		const { siteId } = composerSettings.getComposerSettings();
		const entries = pageAliases.map(pageAlias => {
			const pageId = aliasToPageIdMap?.[pageAlias] ?? pageAlias;
			const isSiteOwnedPage = siteId
				? helpers.getPageOwnershipData(siteId, pageId).isSiteOwnedPage
				: true;
			return {
				pageAlias,
				...(isSiteOwnedPage && {
					noIndex: formData.noIndex,
					noFollow: formData.noFollow
				}),
				...(canEditSitemapCollection && {
					inSitemapHTM: formData.showInHtmlSitemap,
					inSitemapXML: formData.showInXmlSitemap,
					...(formData.showInXmlSitemap && {
						startDate: formData.addToSitemapOn
						? new Date(formData.addToSitemapOn).toISOString()
						: undefined,
					endDate: formData.removeFromSitemapOn
						? new Date(formData.removeFromSitemapOn).toISOString()
							: null
					})
				})
			};
		});
		setIsSaving(true);
		try {
			await SitemapApi.bulkUpdate(entries);

			const hasSiteOwnedPages = entries.some(e => 'noIndex' in e);
			const trackingFields: string[] = [];
			if (canEditSitemapCollection) {
				trackingFields.push(TRACKING_FIELD.HTML, TRACKING_FIELD.XML);
				if (formData.showInXmlSitemap) {
					trackingFields.push(TRACKING_FIELD.XML_ADD_DATE);
					if (formData.removeFromSitemapOn) trackingFields.push(TRACKING_FIELD.XML_REMOVE_DATE);
				}
			}
			if (hasSiteOwnedPages) {
				trackingFields.push(TRACKING_FIELD.NO_INDEX, TRACKING_FIELD.NO_FOLLOW);
			}

			const resultLabel = trackingFields.length
				? `${trackingFields.join(', ')} Saved for ${pageAliases.length} pages`
				: `Saved for ${pageAliases.length} pages`;
			fireTrackableEvent(TRACKING_EVENT.SAVE_BATCH_EDIT, { eventResult: resultLabel });
			onSave?.();
			onClose();
		} catch (err: unknown) {
			const message =
				err instanceof Error
					? err.message
					: intl.formatMessage({ id: 'ERROR' });
			setSaveError(message);
		} finally {
			setIsSaving(false);
		}
	}, [
		formData.showInXmlSitemap,
		formData.addToSitemapOn,
		formData.noIndex,
		formData.noFollow,
		formData.showInHtmlSitemap,
		formData.removeFromSitemapOn,
		pageAliases,
		onSave,
		onClose,
		intl,
		aliasToPageIdMap
	]);

	const handleClose = useCallback(() => {
		if (hasChanges) {
			setIsDiscardDialogOpen(true);
		} else {
			onClose();
		}
	}, [hasChanges, onClose]);

	const handleCancel = useCallback(() => {
		fireTrackableEvent(TRACKING_EVENT.CANCEL_BATCH_EDIT);
		handleClose();
	}, [handleClose]);

	const resetForm = useCallback(() => {
		setFormData({ ...defaultFormData });
		setStartDateError(null);
	}, [defaultFormData]);

	const handleModalCancel = useCallback(() => {
		setIsDiscardDialogOpen(false);
		resetForm();
		onClose();
	}, [onClose, resetForm]);

	const handleModalConfirm = useCallback(() => {
		setIsDiscardDialogOpen(false);
	}, []);

	const handleSaveClick = useCallback(() => {
		if (pageAliases.length > batchUpdateLimit) {
			setIsUpdateLimitDialogOpen(true);
			return;
		}
		handleSave();
	}, [pageAliases.length, batchUpdateLimit, handleSave]);

	const handleCloseUpdateLimitDialog = useCallback(() => {
		setIsUpdateLimitDialogOpen(false);
	}, []);

	return (
		<>
		<SideDrawerLayout
			open={open}
			onClose={handleClose}
			width={drawerWidth}
			offsetLeft={drawerWidth}
			title={intl.formatMessage({ id: 'BATCH_EDIT_PAGE_DETAILS' })}
			subtitle={intl.formatMessage(
				{ id: selectedCount === 1 ? 'PAGE_SELECTED' : 'PAGES_SELECTED' },
				{ count: selectedCount }
			)}
			headerProps={{
				slotProps: {
					closeButton: { sx: { display: 'none' } },
					root: { sx: { bgcolor: '#2C2C2C' } }
				}
			}}
			contentProps={{
				sx: { padding: '8px 8px 8px 12px', bgcolor: '#2C2C2C' }
			}}
			sx={{ zIndex: (t: any) => t.zIndex.drawer - 2 }}
			paperSx={{
				'&::before': {
					content: '""',
					position: 'absolute',
					top: 0,
					left: 0,
					bottom: 0,
					width: '12px',
					background: 'linear-gradient(to right, rgba(0,0,0,0.6), transparent)',
					zIndex: 1,
					pointerEvents: 'none'
				}
			}}
			footer={
				<Box>
					<Box
						sx={{
							display: 'flex',
							gap: 1,
							alignItems: 'center',
							justifyContent: 'flex-end',
							px: 1,
							padding: '4px 8px',
							bgcolor: '#333333',
							borderTop: (t: any) => `1px solid ${t.palette.grey[600]}`
						}}
					>
						<Button
							variant="outlined"
							onClick={handleCancel}
							disabled={isSaving}
						>
							<FormattedMessage id="CANCEL" />
						</Button>
						<Button
							variant="contained"
							onClick={handleSaveClick}
							disabled={isSaving || !!startDateError || !!endDateError}
							startIcon={
								isSaving ? <CircularProgress size={14} color="inherit" /> : null
							}
						>
							<FormattedMessage id="SAVE" />
						</Button>
					</Box>
				</Box>
			}
		>
			<Stack spacing={1}>
				<SaveErrorBanner error={saveError} />
				{canEditSitemapCollection && (
					<>
						<FormControlLabel
							sx={{ width: 'fit-content' }}
							control={
								<Checkbox
									checked={formData.showInHtmlSitemap}
									onChange={handleChange('showInHtmlSitemap')}
									sx={{ py: '6px' }}
								/>
							}
							label={
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 0.5,
										fontSize: 14
									}}
								>
									<FormattedMessage id="SHOW_IN_HTML_SITEMAP" />
									<Tooltip
										title={
											<FormattedMessage id="SHOW_IN_HTML_SITEMAP_TOOLTIP" />
										}
									>
										<InfoOutlined
											sx={{
												fontSize: 12,
												color: 'text.secondary',
												verticalAlign: 'super',
												position: 'relative',
												top: -4
											}}
										/>
									</Tooltip>
								</Box>
							}
						/>

						<FormControlLabel
							sx={{ mt: '0 !important', width: 'fit-content' }}
							control={
								<Checkbox
									checked={formData.showInXmlSitemap}
									onChange={handleChange('showInXmlSitemap')}
									sx={{ py: '6px' }}
								/>
							}
							label={
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 0.5,
										fontSize: 14
									}}
								>
									<FormattedMessage id="SHOW_IN_XML_SITEMAP" />
									<Tooltip
										title={
											<FormattedMessage id="SHOW_IN_XML_SITEMAP_TOOLTIP" />
										}
									>
										<InfoOutlined
											sx={{
												fontSize: 12,
												color: 'text.secondary',
												verticalAlign: 'super',
												position: 'relative',
												top: -4
											}}
										/>
									</Tooltip>
								</Box>
							}
						/>

						{/* Date fields – only shown when Show in XML Sitemap is checked */}
						{formData.showInXmlSitemap && (
							<>
								<Box sx={{ mt: 1.5, ml: '42px !important' }}>
									<Typography variant="body2" sx={{ mb: 0.5, fontSize: 16 }}>
										<FormattedMessage id="SITEMAP_START_DATE" />:
									</Typography>
									<SitemapDatePicker
										placement="bottom-start"
										open={startDateOpen}
										onOpen={() => {
											setEndDateOpen(false);
											setStartDateOpen(true);
										}}
										onClose={() => setStartDateOpen(false)}
										value={formData.addToSitemapOn || null}
										onChange={newValue => {
											setFormData(prev => ({
												...prev,
												addToSitemapOn: newValue
											}));
											if (newValue) {
												setStartDateError(null);
											} else {
												setStartDateError(
													intl.formatMessage({
														id: 'SITEMAP_START_DATE_REQUIRED',
														defaultMessage: 'Start date is required.'
													})
												);
											}
										}}
									/>
									{startDateError && (
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												gap: 0.5,
												mt: 0.5
											}}
										>
											<ErrorOutline
												sx={{ fontSize: 14, color: 'error.main' }}
											/>
											<Typography
												variant="caption"
												color="error.main"
												sx={{ letterSpacing: 'normal', lineHeight: 'normal' }}
											>
												{startDateError}
											</Typography>
										</Box>
									)}
								</Box>

								<Box sx={{ mt: 2, ml: '42px !important' }}>
									<Typography variant="body2" sx={{ mb: 0.5, fontSize: 16 }}>
										<FormattedMessage id="SITEMAP_END_DATE" />:
									</Typography>
									<SitemapDatePicker
										placement="bottom-start"
										open={endDateOpen}
										onOpen={() => {
											setStartDateOpen(false);
											setEndDateOpen(true);
										}}
										onClose={() => setEndDateOpen(false)}
										value={formData.removeFromSitemapOn || null}
										onChange={newValue =>
											setFormData(prev => ({
												...prev,
												removeFromSitemapOn: newValue
											}))
										}
									/>
									{endDateError && (
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												gap: 0.5,
												mt: 0.5
											}}
										>
											<ErrorOutline
												sx={{ fontSize: 14, color: 'error.main' }}
											/>
											<Typography
												variant="caption"
												color="error.main"
												sx={{ letterSpacing: 'normal', lineHeight: 'normal' }}
											>
												{endDateError}
											</Typography>
										</Box>
									)}
								</Box>
							</>
						)}
					</>
				)}
				{canEditSitemapCollection && (
					<Box sx={{ textAlign: 'center', py: 1 }}>
						<Divider>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ textTransform: 'uppercase' }}
							>
								<FormattedMessage id="OR" />
							</Typography>
						</Divider>
					</Box>
				)}

				<FormControlLabel
					sx={{ width: 'fit-content' }}
					control={
						<Checkbox
							checked={formData.noIndex}
							onChange={handleChange('noIndex')}
							sx={{ py: '6px' }}
						/>
					}
					label={
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 0.5,
								fontSize: 14
							}}
						>
							<FormattedMessage id="NO_INDEX" />
							<Tooltip title={<FormattedMessage id="NO_INDEX_TOOLTIP" />}>
								<InfoOutlined
									sx={{
										fontSize: 12,
										color: 'text.secondary',
										verticalAlign: 'super',
										position: 'relative',
										top: -4
									}}
								/>
							</Tooltip>
						</Box>
					}
				/>

				<FormControlLabel
					sx={{ mt: '0 !important', width: 'fit-content' }}
					control={
						<Checkbox
							checked={formData.noFollow}
							onChange={handleChange('noFollow')}
							sx={{ py: '6px' }}
						/>
					}
					label={
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 0.5,
								fontSize: 14
							}}
						>
							<FormattedMessage id="NO_FOLLOW" />
							<Tooltip title={<FormattedMessage id="NO_FOLLOW_TOOLTIP" />}>
								<InfoOutlined
									sx={{
										fontSize: 12,
										color: 'text.secondary',
										verticalAlign: 'super',
										position: 'relative',
										top: -4
									}}
								/>
							</Tooltip>
						</Box>
					}
				/>
			</Stack>
			<ConfirmDialog
				open={isDiscardDialogOpen}
				onConfirm={handleModalConfirm}
				onCancel={handleModalCancel}
				onBackdropClose={handleModalConfirm}
				title={<FormattedMessage id="UNSAVED_CHANGES" />}
				content={<FormattedMessage id="UNSAVED_CHANGES_MESSAGE" />}
				confirmLabel={<FormattedMessage id="STAY" />}
				cancelLabel={<FormattedMessage id="DISCARD_CHANGES" />}
			/>
		</SideDrawerLayout>
		<BatchSizeLimitDialog
			open={isUpdateLimitDialogOpen}
			onClose={handleCloseUpdateLimitDialog}
			title={<FormattedMessage id="BATCH_UPDATE_LIMIT_TITLE" />}
			messages={[
				<FormattedMessage
					key="batch-update-msg-1"
					id="BATCH_UPDATE_LIMIT_MSG_1"
					values={{ batchUpdateLimit }}
				/>,
				<FormattedMessage
					key="batch-update-msg-2"
					id="BATCH_UPDATE_LIMIT_MSG_2"
					values={{ batchUpdateLimit }}
				/>,
				<FormattedMessage
					key="batch-update-msg-3"
					id="BATCH_UPDATE_LIMIT_MSG_3"
					values={{ batchUpdateLimit }}
				/>
			]}
			confirmLabel={<FormattedMessage id="BATCH_LIMIT_CONFIRM" />}
		/>
		</>
	);
};

export default memo(BatchEditPageDetails);
